import React, { FC, useEffect, useState } from 'react'
import { RouteComponentProps } from "@reach/router"
import _ from 'lodash'
import Isvg from 'react-inlinesvg'
// Components
import { Toolbar } from "components/subnav"
import ImportIcon from "components/import-export/import-icon"
import ExportIcon from "components/import-export/export-icon"
import Import from "components/import-export/import"
import RecoveryGroupsTable from "components/recoveries/recovery-groups/recovery-groups-table"
import { WithTip } from 'components/tooltip/with-tip'
// Data
import api_match from "data/interface/api"
import importService from "data/import"
import namedRecoveryService, { Recovery } from "data/named-recoveries"
import recoveryGroupsService, { RecoveryGroup } from "data/recovery-groups"
import propertyService, { Property } from 'data/property'
import { alphanum } from "components/table/lib/sort"
import { ContextRow } from './recovery-groups-table'
import toggles from "feature-toggles"
//Styles
import { RecoveryGroups_s } from "components/recoveries/recovery-groups/recovery-groups-styles"
import { AddIcon } from "images/icons/tools"

const tableId = "recovery-groups-table"

const RecoveryGroups: FC<RouteComponentProps> = (props) => {
  const [data, setData] = useState<RecoveryGroup[]>([])
  const [namedRecoveriesData, setNamedRecoveries] = useState<Recovery[]>([])
  const [initSort, setInitSort] = useState<boolean>(false)
  const [showImport, toggleShowImport] = useState<boolean>(false)
  const [property, setProperty] = useState<Property|null>(null);
  const [propertyId, setPropertyId] = useState<number>(propertyService.getStoredPropertyId())
  const [showSelected, setShowSelected] = useState<boolean>(false)
  const [selected, setSelected] = useState<ContextRow[]>([]);

  useEffect(() => {
    importService.stream().subscribe(data => {
      toggleShowImport(data['showImport'])
    })

    toggleShowImport(false);

    const namedRecoverySubscription = namedRecoveryService.stream().subscribe(data => {
      api_match(
        data,
        init  => setNamedRecoveries(init.value),
        ok    => {
          if(!initSort && ok.value.length) { // sort only on first load
            setNamedRecoveries([...ok.value].sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code"))))
          } else {
            setNamedRecoveries(ok.value)
          }
        },
        err   => {
          setNamedRecoveries(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const recoveryGroupsSubscription = recoveryGroupsService.stream().subscribe(data => {
      api_match(
        data,
        init  => setData(init.value),
        ok    => {
          if(!initSort && ok.value.length) { // sort only on first load
            setData([...ok.value].sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code"))))
            setInitSort(true)
          } else {
            setData(ok.value)
          }
        },
        err   => {
          setData(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const propertySubscription = propertyService.stream().subscribe((data) => {
      if (data.value.length > 0){
        const currentProperty = _.find(data.value, {propertyid: data.current});
        setProperty(currentProperty);
      }
      switch (data.state) {
        case 'ok':
          setPropertyId(data.current!)
          break
      }
    })

    return function cleanup() {
      namedRecoverySubscription.unsubscribe()
      recoveryGroupsSubscription.unsubscribe()
      propertySubscription.unsubscribe()
    }
  }, [initSort])

  useEffect(() => {
    if (_.isNil(propertyId)) return

    namedRecoveryService.fetchNamedRecoveries(propertyId);
    recoveryGroupsService.fetchRecoveryGroups(propertyId);
    propertyService.fetchProperty(propertyId);

  }, [propertyId])

  const Add = () => {
    return (
      <a onClick={() => recoveryGroupsService.create(propertyService.getStoredPropertyId())}>
        <Isvg src={AddIcon} width="20px" />
      </a>
    )
  }

  const [deleteFlag, setDeleteFlag] = useState(0)
  const Delete = () => {
    return (
      <WithTip position="bottom">
        <span>Delete Selected Rows</span>
        <a className={selected.length && !_.some(selected, 'systeminput')  ? '' : 'disabled'}
          onClick={() => {
            if (!selected.length || _.some(selected, 'systeminput')) return
            setDeleteFlag(deleteFlag+1)
        }}>
          <span className="p-menuitem-icon pi pi-fw pi-trash"></span>
        </a>
      </WithTip>
    )
  }

  const [pasteFlag, setPasteFlag] = useState(0)
  const Paste = () => {
    return (
      <WithTip position="bottom">
        <span>Paste to Selected Rows</span>
        <a className={selected.length && !_.some(selected, 'systeminput') ? '' : 'disabled'}
          onClick={() => {
            if (!selected.length || _.some(selected, 'systeminput')) return
            setPasteFlag(pasteFlag+1)
          }
        }>
          <span className="p-menuitem-icon pi pi-fw pi-clone"></span>
        </a>
      </WithTip>
    )
  }

  const ShowSelected = () => (
    <WithTip position="bottom">
      <span>Show {showSelected ? 'All' : 'Selected'} Rows</span>
      <a className={selected.length ? '' : 'disabled'}
        onClick={() => {
          if (!selected.length) return
          setShowSelected(!showSelected)
        }}>
        <span className={showSelected ? 'pi pi-eye-slash' : 'pi pi-eye'} style={{height: '18px', width: '18px'}}></span>
      </a>
    </WithTip>
  )

  useEffect(() => {
    setSelected(_.intersectionBy(data, selected, 'recoverygroupid'))
  }, [data])

  useEffect(() => {
    if (showSelected && _.isEmpty(selected)) setShowSelected(false)
  }, [selected])

  const _data = showSelected ? selected : data

  return (
    <RecoveryGroups_s>
      <Toolbar>
        <Add />
        {toggles.multiselect() && <>
          <Paste />
          <Delete />
          <ShowSelected />
        </>}
      </Toolbar>
      { showImport && <Import /> }
      { data && property && namedRecoveriesData && !showImport && <RecoveryGroupsTable tableId={tableId} data={_data} property={property} namedRecoveriesData={namedRecoveriesData} selectedState={[selected, setSelected]} flags={{deleteFlag, pasteFlag}} /> }
    </RecoveryGroups_s>
  )
}

export default RecoveryGroups
